import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useScrollDirection, { SCROLL_DIRECTION_UP } from '../hooks/useScrollDirection';
import { NavLink, Route, Switch } from 'react-router-dom';
import remToPixels from '../utils/remToPixels';
import JwtStore from '../flux/JwtStore';
import { default as HomeIcon } from '../icons/Home';
import { default as HomeFillIcon } from '../icons/HomeFill';
import { default as SeasonalIcon } from '../icons/Seasonal';
import { default as SeasonalFillIcon } from '../icons/SeasonalFill';
import { default as ManualsIcon } from '../icons/Manuals';
import { default as ManualsFillIcon } from '../icons/ManualsFill';
import { default as TimelessIcon } from '../icons/Timeless';
import { default as TimelessFillIcon } from '../icons/TimelessFill';
import { default as ProductFocusIcon } from '../icons/ProductFocus';
import { default as ProductFocusFillIcon } from '../icons/ProductFocusFill';
import ModalFullscreenStore from '../flux/ModalFullscreenStore';
import { withRouter } from "react-router-dom";
import RouterStore from "../flux/RouterStore";
import ModalFullscreenActions from "../flux/ModalFullscreenActions";

const navLinkStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  lineHeight: 1,
  fontSize: '.625rem',
  textAlign: 'center',
  color: 'rgb(0 0 0 / .4)', // Should be rgb(0 0 0 / .3), but that's OK
};

const navLinkActiveStyle = {
  color: '#000',
  opacity: 'initial',
};

const iconStyle = {
  width: '1.75rem',
  height: '1.75rem',
  flexShrink: 0,
};

function TabBar({
  location
}) {
  const scrollDirection = useScrollDirection(
    remToPixels(3.25),
    remToPixels(3.25)
  );

  const [modalFullscreen, setModalFullscreen] = useState(ModalFullscreenStore.getState());

  const [modalFullScreenPath, setModalFullScreenPath] = useState(RouterStore.getState().modal)

  useEffect(function() {
    const subscription = ModalFullscreenStore.addListener(function() {
      setModalFullscreen(ModalFullscreenStore.getState());
    });

    return function() {
      subscription.remove();
    }
  }, []);

  useEffect(function() {
    const subscription = RouterStore.addListener(function () {
      setModalFullScreenPath(RouterStore.getState().modal);
    });

    return function () {
      subscription.remove();
    }
  }, []);

  const isVisible = (
    scrollDirection === null ||
    scrollDirection === SCROLL_DIRECTION_UP ||
    modalFullscreen.isVisible
  );

  const isActive = (modalFullScreenPath !== null) ? () => false : undefined;
  const homePathNames = ['/hot-now', '/to-complete', '/never-seen'];
  const homeIsActive = (modalFullScreenPath !== null) ?
    () => false :
    () => (homePathNames.indexOf(location.pathname) > -1);

  return (
    <TabBarContainer isVisible={isVisible}>
      <TabContainerInner isVisible={isVisible}>
        <NavLink
          isActive={homeIsActive}
          style={navLinkStyle}
          activeStyle={navLinkActiveStyle}
          exact
          to="/hot-now"
          onClick={() => ModalFullscreenActions.close()}
        >
          <NavLinkIcon isActive={homeIsActive} active={<HomeFillIcon style={iconStyle} />}>
            <HomeIcon style={iconStyle} />
          </NavLinkIcon>
          <span style={{ paddingTop: '.25rem' }}>Home</span>
        </NavLink>

        <NavLink
          isActive={isActive}
          style={navLinkStyle}
          activeStyle={navLinkActiveStyle}
          to="/seasonal"
          onClick={() => ModalFullscreenActions.close()}
        >
          <NavLinkIcon isActive={isActive} path="/seasonal" active={<SeasonalFillIcon style={iconStyle} />}>
            <SeasonalIcon style={iconStyle} />
          </NavLinkIcon>
          <span style={{ paddingTop: '.25rem' }}>Seasonal</span>
        </NavLink>

        <NavLink
          isActive={isActive}
          style={navLinkStyle}
          activeStyle={navLinkActiveStyle}
          to="/manuals"
          onClick={() => ModalFullscreenActions.close()}
        >
          <NavLinkIcon isActive={isActive} path="/manuals" active={<ManualsFillIcon style={iconStyle} />}>
            <ManualsIcon style={iconStyle} />
          </NavLinkIcon>
          <span style={{ paddingTop: '.25rem' }}>Manuals</span>
        </NavLink>

        <NavLink
          isActive={isActive}
          style={navLinkStyle}
          activeStyle={navLinkActiveStyle}
          to="/timeless"
          onClick={() => ModalFullscreenActions.close()}
        >
          <NavLinkIcon isActive={isActive} path="/timeless" active={<TimelessFillIcon style={iconStyle} />}>
            <TimelessIcon style={iconStyle} />
          </NavLinkIcon>
          <span style={{ paddingTop: '.25rem' }}>Timeless</span>
        </NavLink>

        {JwtStore.isAvailableFeature('product-focus') && (
          <NavLink
            isActive={isActive}
            style={navLinkStyle}
            activeStyle={navLinkActiveStyle}
            to="/product-focuses"
            onClick={() => ModalFullscreenActions.close()}
          >
            <NavLinkIcon isActive={isActive} path="/product-focuses" active={<ProductFocusFillIcon style={iconStyle} />}>
              <ProductFocusIcon style={iconStyle} />
            </NavLinkIcon>
            <span style={{ paddingTop: '.25rem' }}>Product Focus</span>
          </NavLink>
        )}
      </TabContainerInner>
    </TabBarContainer>
  )
}

export default withRouter(TabBar)

const TabBarContainer = styled.div`
  pointer-events: ${({ isVisible }) => isVisible ? null : 'none'};
  z-index: 109; // same as ModalFullscreen
  overflow-y: hidden;
  position: -webkit-sticky;
  position: sticky;
  right: 0;
  bottom: 0;
  left: 0;
  box-shadow: 0 0 .375rem rgb(0 0 0 / ${({ isVisible }) => isVisible ? .25 : 0});
  transition: box-shadow ${({ isVisible }) => isVisible ? '300ms' : '0'} ease-in-out;
  transition-delay: ${({ isVisible }) => isVisible ? '300ms' : '0'};
  will-change: box-shadow;
`;

const TabContainerInner = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  background-color: #fff;
  padding: 1rem 1rem max(1rem, env(safe-area-inset-bottom)) 1rem;

  transform: translateY(${({ isVisible }) => isVisible ? '0' : '100%' });
  transition: transform 300ms ease-in-out;
  will-change: transform;
`;

function NavLinkIcon({
  active,
  children,
  isActive,
  ...props
}) {
  if (typeof isActive === 'function' && isActive() === false) {
    return children;
  }

  if (typeof isActive === 'function' && isActive() === true) {
    return active;
  }

  return (
    <Switch>
      <Route {...props}>
        {active}
      </Route>
      <Route path="/">
        {children}
      </Route>
    </Switch>
  )
}
